import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ListingTypeClient, ListingTypeDTO } from '../services/apiClient';
import { API_BASE_URL } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new ListingTypeClient(API_BASE_URL, axiosInstance);
const MAX_DROPDOWN_PAGE_SIZE = 9999;

// Cached hook for dropdowns - get all active listing types
export const useListingTypes = () => {
  return useQuery({
    queryKey: ['listingTypes', 'all'],
    queryFn: async () => {
      const response = await apiClient.searchListingTypes({
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });
};

// Non-cached search hook for list pages
export const useListingTypeSearch = () => {
  const search = async (params: any) => {
    const response = await apiClient.searchListingTypes(params);
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };

  return { search };
};

// Get by ID
export const useListingTypeById = (id: string | undefined) => {
  return useQuery({
    queryKey: ['listingTypes', id],
    queryFn: async () => {
      if (!id) return null;
      const response = await apiClient.getListingTypeById(id);
      return response?.data || null;
    },
    enabled: !!id,
  });
};

// Create mutation
export const useCreateListingType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ListingTypeDTO) => {
      return await apiClient.createListingType(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listingTypes'] });
    },
  });
};

// Update mutation
export const useUpdateListingType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ListingTypeDTO }) => {
      return await apiClient.updateListingType(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listingTypes'] });
    },
  });
};

// Delete mutation
export const useDeleteListingType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return await apiClient.deleteListingType(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listingTypes'] });
    },
  });
};